import { useState, type ReactNode } from 'react';
import type { TaskInput, TaskSummary } from '../../lib/tasks';
import {
  TASK_STATUSES,
  TASK_STATUS_DOT_CLASSES,
  TASK_STATUS_LABELS,
  type TaskStatus,
} from '../../lib/taskStatus';
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_DOT_CLASSES,
  TASK_PRIORITY_LABELS,
  type TaskPriority,
} from '../../lib/taskPriority';
import { pickerOptions, type Assignee } from '../../lib/assignees';
import { instantToLocalInput, localInputToInstant, todayInputValue } from '../../lib/taskDates';
import type { RepeatRule } from '../../lib/recurrence';
import {
  notificationPermission,
  requestNotificationPermission,
  type NotificationPermissionState,
} from '../../lib/reminders';
import { AssigneePicker } from './AssigneePicker';
import { Select, Button } from '../ui';
import { DateInput } from '../DateInput';
import { TagInput } from '../projects/TagInput';
import { TaskCombobox } from './TaskCombobox';
import { RepeatPicker } from './RepeatPicker';
import {
  BellIcon,
  CalendarIcon,
  CloseIcon,
  PriorityIcon,
  RepeatIcon,
  StatusIcon,
  SubtaskIcon,
  TagIcon,
  UserIcon,
  UsersIcon,
} from '../icons';

/**
 * Everything about a task that is a plain form control, decoupled from whether
 * the task exists yet.
 *
 * A saved task holds these on the server and a draft holds them in local state,
 * so this takes a value object rather than a `Task` -- one set of controls for
 * both, instead of a second copy that drifts from this one.
 */
export type TaskFieldValues = {
  status: TaskStatus;
  priority: TaskPriority;
  tags: string[];
  /** Calendar dates as `YYYY-MM-DD`, which is what `<input type="date">` speaks. */
  startDate: string | null;
  dueDate: string | null;
  /** An ISO instant, the wire format -- converted at the input's edge rather
   *  than here, so what this object holds is what gets sent. */
  remindAt: string | null;
  repeat: RepeatRule | null;
  ownerId: string | null;
  collaboratorIds: string[];
  parent: { id: string; title: string } | null;
};

/**
 * One property: an icon and a name on the left, the control on the right.
 *
 * Labels above controls cost a 20px line and a 6px gap each, and eight of them
 * turned the panel into something you had to scroll to submit. Beside the
 * control they cost nothing extra and the panel reads as one property sheet
 * rather than eight unrelated fields.
 *
 * The label is padded rather than vertically centred, so it lines up with the
 * control's *first* line -- the chip pickers grow to two and three rows as
 * people and tags are added, and centring would slide the word away from the
 * box it names.
 */
function PropertyRow({
  icon,
  label,
  htmlFor,
  children,
}: {
  icon: ReactNode;
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    // One column below `sm:`: the panel is full-width on a phone, where a fixed
    // label track leaves too little for the two date inputs to sit side by side.
    //
    // 7rem and not 6: "Collaborators" plus its icon and gap measures just over
    // 6rem in Inter, so at the old width that one row's label ran flush into
    // its field while every other row had a gutter.
    <div className="grid grid-cols-1 gap-x-2 sm:grid-cols-[7rem_minmax(0,1fr)]">
      <label
        htmlFor={htmlFor}
        className="flex items-center gap-1.5 py-1 text-dense text-stone-500"
      >
        <span className="shrink-0 text-stone-400">{icon}</span>
        {label}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/**
 * Whether the browser will actually deliver the reminder just set, and the one
 * chance to fix it if not.
 *
 * Shown only once there is a reminder to deliver. Asking for notification
 * permission before anybody has asked to be notified is the prompt everyone
 * blocks on reflex, and a blocked prompt cannot be re-opened from script -- so
 * the cost of asking early is the feature, permanently.
 *
 * A button rather than a request fired when a time is typed: Safari refuses a
 * permission request that did not come from a user gesture, and the other
 * browsers are moving the same way.
 */
function NotificationConsent() {
  const [permission, setPermission] = useState<NotificationPermissionState>(notificationPermission);

  if (permission === 'granted') return null;

  if (permission === 'default') {
    return (
      <div className="mt-1.5">
        <Button
          compact
          type="button"
          variant="secondary"
          onClick={() => {
            void requestNotificationPermission().then(setPermission);
          }}
        >
          Enable notifications
        </Button>
      </div>
    );
  }

  return (
    <p className="mt-1.5 text-meta text-stone-500">
      {permission === 'unsupported'
        ? 'This browser cannot show notifications, so the reminder will not appear.'
        : 'Notifications are blocked for this site, so the reminder will not appear. Allow them in the browser\u2019s site settings.'}
    </p>
  );
}

/**
 * A native select with the value's colour dot sitting inside it.
 *
 * The list already speaks in these colours -- a status pill and a priority dot
 * on every row -- and the panel that sets them showed two identical grey boxes.
 * Overlaid rather than built as a custom listbox: a `<select>` keeps the native
 * keyboard behaviour and the platform picker on mobile, which is worth more here
 * than a stylable dropdown.
 *
 * `pl-7` clears the dot. It beats the skin's `px-2.5` regardless of source order,
 * because Tailwind emits `padding-inline` before `padding-inline-start`.
 */
function DotSelect({
  id,
  value,
  dotClass,
  onChange,
  children,
}: {
  id: string;
  value: string;
  dotClass: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <div className="relative">
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute left-2.5 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full ${dotClass}`}
      />
      <Select
        compact
        id={id}
        value={value}
        className="pl-7"
        onChange={(event) => onChange(event.target.value)}
      >
        {children}
      </Select>
    </div>
  );
}

/**
 * The property sheet.
 *
 * For a saved task every control writes straight through -- there is no Save
 * button, because a twelve-field panel that can be dismissed by clicking the
 * list behind it would otherwise lose edits. A draft routes the same callbacks
 * into local state and saves once. This is why it diverges from ProjectForm's
 * submit-everything model.
 *
 * Dates need no conversion in either direction: `<input type="date">` reads and
 * writes `YYYY-MM-DD`, which is exactly the wire format. They go through
 * DateInput rather than `onChange`, though: written through on every change,
 * paging the calendar to another month saved a date per page.
 */
export function TaskFields({
  idPrefix,
  values,
  tasks,
  excludeFromParent,
  assignees,
  tagSuggestions,
  onChange,
  onParentChange,
  nextInstanceHref = null,
}: {
  /** Namespaces the control ids, so two panels could coexist without colliding. */
  idPrefix: string;
  values: TaskFieldValues;
  tasks: TaskSummary[];
  /** Ids a parent may not be: the task itself and its own descendants. */
  excludeFromParent: Set<string>;
  assignees: Assignee[];
  tagSuggestions: string[];
  onChange: (patch: Partial<TaskInput>) => void;
  /** Separate from `onChange` because a draft needs the title to display, not
   *  just the id it would send. */
  onParentChange: (picked: TaskSummary | null) => void;
  /** Where this task's successor lives, once it has spawned one -- which is
   *  what turns its Repeat row read-only. A draft has none. */
  nextInstanceHref?: string | null;
}) {
  const parentOptions = tasks.filter(
    (candidate) =>
      !excludeFromParent.has(candidate.id) && candidate.id !== values.parent?.id,
  );

  return (
    // Boxed and tinted so the properties read as one block. In the detail panel
    // this is also what keeps them from blurring into the Prerequisites,
    // Subtasks and Comments sections that follow -- the controls stay white and
    // lift off the wash.
    <div className="space-y-0.5 rounded-xl border border-stone-200 bg-stone-50 p-2.5">
      <PropertyRow icon={<StatusIcon />} label="Status" htmlFor={`status-${idPrefix}`}>
        <DotSelect
          id={`status-${idPrefix}`}
          value={values.status}
          dotClass={TASK_STATUS_DOT_CLASSES[values.status]}
          onChange={(status) => onChange({ status: status as TaskStatus })}
        >
          {TASK_STATUSES.map((status) => (
            <option key={status} value={status}>
              {TASK_STATUS_LABELS[status]}
            </option>
          ))}
        </DotSelect>
      </PropertyRow>

      <PropertyRow icon={<PriorityIcon />} label="Priority" htmlFor={`priority-${idPrefix}`}>
        <DotSelect
          id={`priority-${idPrefix}`}
          value={values.priority}
          dotClass={TASK_PRIORITY_DOT_CLASSES[values.priority]}
          onChange={(priority) => onChange({ priority: priority as TaskPriority })}
        >
          {TASK_PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {TASK_PRIORITY_LABELS[priority]}
            </option>
          ))}
        </DotSelect>
      </PropertyRow>

      <PropertyRow icon={<UserIcon />} label="Owner" htmlFor={`owner-${idPrefix}`}>
        <Select
          compact
          id={`owner-${idPrefix}`}
          value={values.ownerId ?? ''}
          onChange={(event) => onChange({ ownerId: event.target.value || null })}
        >
          <option value="">Nobody</option>
          {/* Active people, plus whoever is already selected even if archived --
              otherwise the archived name is not among the options, the select
              falls back to empty, and the next unrelated edit silently clears
              the assignment. */}
          {pickerOptions(assignees, values.ownerId ? [values.ownerId] : []).map((assignee) => (
            <option key={assignee.id} value={assignee.id}>
              {assignee.name}
              {assignee.archived ? ' (archived)' : ''}
            </option>
          ))}
        </Select>
      </PropertyRow>

      <PropertyRow icon={<SubtaskIcon />} label="Parent" htmlFor={`parent-${idPrefix}`}>
        {values.parent ? (
          // A chip, matching how a tag and a collaborator are shown one row
          // below -- three ways to say "this is set, here is how to unset it"
          // was two too many.
          <div className="flex min-h-[1.75rem] items-center">
            <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-stone-100 py-0.5 pl-2 pr-1 text-xs font-medium text-stone-700">
              <span className="truncate">{values.parent.title}</span>
              <button
                type="button"
                onClick={() => onParentChange(null)}
                aria-label={`Clear parent ${values.parent.title}`}
                className="rounded-full px-1 text-stone-500 hover:bg-stone-200 hover:text-stone-900"
              >
                <CloseIcon className="h-3 w-3" />
              </button>
            </span>
          </div>
        ) : (
          <TaskCombobox
            compact
            id={`parent-${idPrefix}`}
            label="Parent task"
            options={parentOptions}
            placeholder="Search tasks…"
            onSelect={(picked) => onParentChange(picked)}
          />
        )}
      </PropertyRow>

      {/* One row for both ends of the range. Two rows spent a whole label line
          each to say "date" twice, and start-then-due already reads as a span. */}
      <PropertyRow icon={<CalendarIcon />} label="Dates" htmlFor={`start-${idPrefix}`}>
        <div className="flex flex-wrap items-center gap-2">
          {/* Sized by a wrapper rather than by passing `w-auto`: the skin
              already sets `w-full`, and which of two width utilities wins is
              decided by Tailwind's emit order, not by who was appended last. */}
          <div className="min-w-0 flex-1">
            <DateInput
              compact
              id={`start-${idPrefix}`}
              // The visible label says "Dates" and belongs to the pair, so each
              // end names itself.
              aria-label="Start date"
              value={values.startDate ?? ''}
              onCommit={(startDate) => onChange({ startDate: startDate || null })}
            />
          </div>
          <span aria-hidden="true" className="shrink-0 text-xs text-stone-400">
            →
          </span>
          <div className="min-w-0 flex-1">
            <DateInput
              compact
              id={`due-${idPrefix}`}
              aria-label="Due date"
              value={values.dueDate ?? ''}
              onCommit={(dueDate) => onChange({ dueDate: dueDate || null })}
            />
          </div>
        </div>
      </PropertyRow>

      {/* Its own row rather than a third box in the one above. The dates are a
          span and read as one thing; this is a moment, it is the only field here
          the browser acts on by itself, and it is the only one that can need
          permission before it works. */}
      <PropertyRow icon={<BellIcon />} label="Remind me" htmlFor={`remind-${idPrefix}`}>
        <DateInput
          compact
          id={`remind-${idPrefix}`}
          type="datetime-local"
          value={values.remindAt ? instantToLocalInput(values.remindAt) : ''}
          onCommit={(remindAt) =>
            onChange({ remindAt: remindAt ? localInputToInstant(remindAt) : null })
          }
        />
        {values.remindAt ? <NotificationConsent /> : null}
      </PropertyRow>

      <PropertyRow icon={<RepeatIcon />} label="Repeat" htmlFor={`repeat-${idPrefix}`}>
        <RepeatPicker
          id={`repeat-${idPrefix}`}
          value={values.repeat}
          nextInstanceHref={nextInstanceHref}
          onChange={(repeat) =>
            // A rule steps from the due date, so one with nothing to step from
            // starts today -- in the same patch, so the server never sees the
            // rule without the date it needs.
            onChange(
              repeat && !values.dueDate ? { repeat, dueDate: todayInputValue() } : { repeat },
            )
          }
        />
      </PropertyRow>

      <PropertyRow
        icon={<UsersIcon />}
        label="Collaborators"
        htmlFor={`collaborators-${idPrefix}`}
      >
        <AssigneePicker
          compact
          id={`collaborators-${idPrefix}`}
          label="Collaborators"
          assignees={assignees}
          // The owner is theirs alone; showing or offering them here would mean
          // two controls disagreeing about the same person. The server strips
          // them either way -- this stops the pick ever looking like it worked.
          selectedIds={values.collaboratorIds.filter((id) => id !== values.ownerId)}
          excludeIds={values.ownerId ? [values.ownerId] : []}
          onChange={(collaboratorIds) => onChange({ collaboratorIds })}
        />
      </PropertyRow>

      <PropertyRow icon={<TagIcon />} label="Tags" htmlFor={`tags-${idPrefix}`}>
        <TagInput
          compact
          id={`tags-${idPrefix}`}
          tags={values.tags}
          suggestions={tagSuggestions}
          onChange={(tags) => onChange({ tags })}
        />
      </PropertyRow>
    </div>
  );
}
